import { useState, useEffect } from "react";
import { doc, getDoc, setDoc, updateDoc, arrayUnion } from "firebase/firestore";
import { db } from "../firebase";
import { generateId } from "../utils/idUtils";

/**
 * useFamily — maneja familias, miembros, managed members, santa secreto y nicknames
 *
 * @param {object} params
 * @param {object} params.user               — usuario autenticado
 * @param {object} params.userProfile        — perfil del usuario
 * @param {function} params.setUserProfile   — setter del perfil
 * @param {string} params.lang               — idioma activo
 * @param {function} params.showToast        — función para mostrar toasts
 * @param {function} params.tr               — función de traducción
 *
 * @returns {object} estado y handlers de familia
 */
export function useFamily({ user, userProfile, setUserProfile, lang, showToast, tr }) {

  // ── Estado ──
  const [currentFamily, setCurrentFamily] = useState(() => {
    try { const c = localStorage.getItem("whislu_family"); return c ? JSON.parse(c) : null; } catch (e) { return null; }
  });
  const [userFamilies, setUserFamilies] = useState(() => {
    try { const c = localStorage.getItem("whislu_families"); return c ? JSON.parse(c) : []; } catch (e) { return []; }
  });
  const [members, setMembers] = useState(() => {
    try { const c = localStorage.getItem("whislu_members"); return c ? JSON.parse(c) : []; } catch (e) { return []; }
  });
  const [groupIcons, setGroupIcons] = useState(() => {
    try { const c = localStorage.getItem("whislu_group_icons"); return c ? JSON.parse(c) : {}; } catch (e) { return {}; }
  });

  // modales / ui state relacionado con familia
  const [showAddManaged, setShowAddManaged] = useState(false);
  const [showEditName, setShowEditName] = useState(false);
  const [showSantaConfirm, setShowSantaConfirm] = useState(false);
  const [showSantaEnvelope, setShowSantaEnvelope] = useState(false);
  const [editingManaged, setEditingManaged] = useState(null);
  const [editingNickname, setEditingNickname] = useState(false);
  const [nicknameValue, setNicknameValue] = useState("");
  const [managedName, setManagedName] = useState("");
  const [managedColor, setManagedColor] = useState("#e67e22");
  const [santaBudget, setSantaBudget] = useState("");
  const [santaRevealName, setSantaRevealName] = useState("");
  const [editNameValue, setEditNameValue] = useState("");
  const [editNameOpenedFrom, setEditNameOpenedFrom] = useState("menu");
  const [editingGroupIcon, setEditingGroupIcon] = useState("");
  const [popupFamily, setPopupFamily] = useState(null);
  const [wishlistNames, setWishlistNames] = useState({});

  // ── Persistencia de currentFamily ──
  useEffect(() => {
    if (!currentFamily) return;
    try { localStorage.setItem("whislu_family", JSON.stringify(currentFamily)); } catch (e) {}
  }, [currentFamily]);

  // ── Cargar miembros cuando cambia la familia ──
  useEffect(() => {
    if (!currentFamily?.id) { setMembers([]); return; }

    async function loadMembers() {
      const fSnap = await getDoc(doc(db, "families", currentFamily.id));
      if (!fSnap.exists()) return;
      const familyData = fSnap.data();
      const memberIds = familyData.memberIds || [];

      const memberSnaps = await Promise.all(memberIds.map((uid) => getDoc(doc(db, "users", uid))));

      // recopilar wishlist ids necesarios
      const wishlistFetches = {};
      memberSnaps.forEach((ms) => {
        if (!ms.exists()) return;
        const wid = (ms.data().familyWishlists || {})[currentFamily.id];
        if (wid) wishlistFetches[ms.id] = wid;
      });

      const managedRaw = familyData.managedMembers || [];
      managedRaw.forEach((raw) => {
        try {
          const m = typeof raw === "string" ? JSON.parse(raw) : raw;
          if (m.wishlistId) wishlistFetches["_m_" + m.id] = m.wishlistId;
        } catch (e) {}
      });

      const wishlistIds = Object.values(wishlistFetches);
      const wSnaps = await Promise.all(wishlistIds.map((wid) => getDoc(doc(db, "wishlists", wid))));
      const wishlistData = {};
      wishlistIds.forEach((wid, i) => {
        if (wSnaps[i].exists()) wishlistData[wid] = wSnaps[i].data().gifts || [];
      });

      const list = [];

      memberSnaps.forEach((ms) => {
        if (!ms.exists()) return;
        const userData = ms.data();
        const wishlistId = (userData.familyWishlists || {})[currentFamily.id];
        list.push({ id: ms.id, ...userData, gifts: wishlistId ? (wishlistData[wishlistId] || []) : [], wishlistId });
      });

      managedRaw.forEach((raw) => {
        try {
          const m = typeof raw === "string" ? JSON.parse(raw) : raw;
          const wid = m.wishlistId;
          list.push({ ...m, gifts: wid ? (wishlistData[wid] || []) : [], isManaged: true });
        } catch (e) {}
      });

      list.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
      setMembers(list);
      try { localStorage.setItem("whislu_members", JSON.stringify(list)); } catch (e) {}
    }

    loadMembers();
  }, [currentFamily?.id]);

  // ── Helpers ──
  const getDisplayName = (member) => {
    if (!member) return "";
    const fid = currentFamily?.id;
    if (fid && member.groupNicknames && member.groupNicknames[fid]) return member.groupNicknames[fid];
    return member.name || "";
  };

  const isLeader = currentFamily && user && currentFamily.leaderId === user.uid;

  // ── Nickname de grupo ──
  const saveGroupNickname = async () => {
    if (!user || !currentFamily?.id) return;
    const fid = currentFamily.id;
    const trimmed = nicknameValue.trim();
    const newNicknames = { ...(userProfile?.groupNicknames || {}), [fid]: trimmed };
    await updateDoc(doc(db, "users", user.uid), { groupNicknames: newNicknames });
    setUserProfile((p) => ({ ...p, groupNicknames: newNicknames }));
    setMembers((ms) => ms.map((m) => m.id === user.uid ? { ...m, groupNicknames: newNicknames } : m));
    setEditingNickname(false);
    showToast(lang === "es" ? "✓ Nickname guardado" : "✓ Nickname saved");
  };

  // ── Managed members ──
  const handleAddManagedMember = async () => {
    if (!managedName.trim() || !currentFamily || !user) return;
    const managedId = "managed_" + user.uid + "_" + generateId();
    const wishlistId = generateId();
    await setDoc(doc(db, "wishlists", wishlistId), { ownerId: managedId, name: managedName.trim() + "'s list", gifts: [] });
    const managedEntry = { id: managedId, name: managedName.trim(), color: managedColor, managedBy: user.uid, familyId: currentFamily.id, wishlistId };
    const existingManaged = { ...(userProfile?.managedMembers || {}) };
    existingManaged[managedId] = managedEntry;
    await updateDoc(doc(db, "users", user.uid), { managedMembers: existingManaged });
    await updateDoc(doc(db, "families", currentFamily.id), { managedMembers: arrayUnion(JSON.stringify(managedEntry)) });
    setUserProfile((p) => ({ ...p, managedMembers: existingManaged }));
    setMembers((ms) => [...ms, { ...managedEntry, gifts: [], wishlistId, isManaged: true }].sort((a, b) => a.name.localeCompare(b.name)));
    setShowAddManaged(false);
    setManagedName("");
    setManagedColor("#e67e22");
    showToast((lang === "es" ? "¡" : "") + managedName.trim() + (lang === "es" ? " agregado!" : " added!"));
  };

  const saveEditedManaged = async () => {
    if (!editingManaged || !editingManaged.name.trim()) return;
    const fSnap = await getDoc(doc(db, "families", currentFamily.id));
    const existing = fSnap.data().managedMembers || [];
    const updated = existing.map((raw) => {
      try {
        const m = typeof raw === "string" ? JSON.parse(raw) : raw;
        if (m.id !== editingManaged.id) return raw;
        return JSON.stringify({ ...m, name: editingManaged.name.trim(), color: editingManaged.color });
      } catch (e) { return raw; }
    });
    await updateDoc(doc(db, "families", currentFamily.id), { managedMembers: updated });
    const newManaged = { ...(userProfile?.managedMembers || {}) };
    if (newManaged[editingManaged.id]) {
      newManaged[editingManaged.id] = { ...newManaged[editingManaged.id], name: editingManaged.name.trim(), color: editingManaged.color };
    }
    await updateDoc(doc(db, "users", user.uid), { managedMembers: newManaged });
    setUserProfile((p) => ({ ...p, managedMembers: newManaged }));
    setMembers((ms) => ms.map((m) => m.id === editingManaged.id ? { ...m, name: editingManaged.name.trim(), color: editingManaged.color } : m));
    setEditingManaged(null);
    showToast(tr("familySaved"));
  };

  const deleteManagedMember = async (managedId, setScreen) => {
    if (!window.confirm(tr("deleteManagedConfirm"))) return;
    const fSnap = await getDoc(doc(db, "families", currentFamily.id));
    const existing = fSnap.data().managedMembers || [];
    const updatedList = existing.filter((raw) => {
      try { return (typeof raw === "string" ? JSON.parse(raw) : raw).id !== managedId; } catch (e) { return true; }
    });
    await updateDoc(doc(db, "families", currentFamily.id), { managedMembers: updatedList });
    const newManaged = { ...(userProfile?.managedMembers || {}) };
    delete newManaged[managedId];
    await updateDoc(doc(db, "users", user.uid), { managedMembers: newManaged });
    setUserProfile((p) => ({ ...p, managedMembers: newManaged }));
    setMembers((ms) => ms.filter((m) => m.id !== managedId));
    if (setScreen) setScreen("family");
    showToast(tr("memberRemoved").replace("{name}", ""));
  };

  // ── Grupo: crear / editar / eliminar / salir ──
  const saveGroupName = async (familyId, newName) => {
    if (!newName.trim()) return;
    await updateDoc(doc(db, "families", familyId), { name: newName.trim() });
    setCurrentFamily((f) => ({ ...f, name: newName.trim() }));
    setUserFamilies((fs) => fs.map((f) => f.id === familyId ? { ...f, name: newName.trim() } : f));
    showToast(tr("familySaved"));
  };

  const saveGroupIcon = (familyId, emoji) => {
    const updated = { ...groupIcons, [familyId]: emoji };
    setGroupIcons(updated);
    try { localStorage.setItem("whislu_group_icons", JSON.stringify(updated)); } catch (e) {}
  };

  const deleteGroup = async (familyId) => {
    if (!window.confirm(tr("deleteGroupConfirm"))) return;
    try {
      const fSnap = await getDoc(doc(db, "families", familyId));
      if (fSnap.exists()) {
        const memberIds = fSnap.data().memberIds || [];
        await Promise.all(memberIds.map((mid) =>
          updateDoc(doc(db, "users", mid), { families: (userProfile?.families || []).filter((id) => id !== familyId) })
        ));
        await updateDoc(doc(db, "families", familyId), { deleted: true, memberIds: [] });
      }
    } catch (e) { console.error("deleteGroup cleanup error", e); }
    const remaining = userFamilies.filter((f) => f.id !== familyId);
    setUserFamilies(remaining);
    setUserProfile((p) => ({ ...p, families: (p.families || []).filter((id) => id !== familyId) }));
    setCurrentFamily(remaining[0] || null);
    showToast(tr("deleteGroup"));
  };

  const leaveGroup = async (familyId) => {
    if (!window.confirm(tr("leaveGroupConfirm"))) return;
    const currentIds = (userProfile?.families || []).filter((id) => id !== familyId);
    const fSnap = await getDoc(doc(db, "families", familyId));
    const newMemberIds = (fSnap.data()?.memberIds || []).filter((id) => id !== user.uid);
    await updateDoc(doc(db, "families", familyId), { memberIds: newMemberIds });
    await updateDoc(doc(db, "users", user.uid), { families: currentIds });
    setUserProfile((p) => ({ ...p, families: currentIds }));
    setUserFamilies((fs) => fs.filter((f) => f.id !== familyId));
    setCurrentFamily(userFamilies.filter((f) => f.id !== familyId)[0] || null);
    setMembers((ms) => ms.filter((m) => m.id !== user.uid));
    showToast(tr("leaveGroup"));
  };

  // ── Santa Secreto ──
  const activateSantaSecreto = async () => {
    if (members.length < 2) { showToast(tr("santaSecretNotEnough")); return; }
    const ids = members.map((m) => m.id);
    let shuffled, attempts = 0;
    do {
      shuffled = [...ids].sort(() => Math.random() - 0.5);
      attempts++;
    } while (shuffled.some((id, i) => id === ids[i]) && attempts < 100);
    const assignments = {};
    ids.forEach((id, i) => { assignments[id] = shuffled[i]; });
    await updateDoc(doc(db, "families", currentFamily.id), {
      santaSecreto: { active: true, assignments, budget: santaBudget || "", activatedAt: Date.now() }
    });
    setCurrentFamily((f) => ({ ...f, santaSecreto: { active: true, assignments, budget: santaBudget || "", activatedAt: Date.now() } }));
    setShowSantaConfirm(false);
    setSantaBudget("");
    showToast("🎅 " + tr("santaSecretActive"));
  };

  const deactivateSantaSecreto = async () => {
    await updateDoc(doc(db, "families", currentFamily.id), { santaSecreto: { active: false } });
    setCurrentFamily((f) => ({ ...f, santaSecreto: { active: false } }));
    showToast(tr("santaSecretDeactivate"));
  };

  const openSantaEnvelope = () => {
    const santa = currentFamily?.santaSecreto;
    if (!santa?.active || !santa.assignments) return;
    const myManagedIds = members.filter((m) => m.isManaged && m.managedBy === user.uid).map((m) => m.id);
    const allMyIds = [user.uid, ...myManagedIds];
    const reveals = [];
    for (const gifterId of allMyIds) {
      const assignedId = santa.assignments[gifterId];
      if (!assignedId) continue;
      const gifterMember = members.find((m) => m.id === gifterId);
      const recipientMember = members.find((m) => m.id === assignedId);
      if (recipientMember) {
        reveals.push({
          gifterName: gifterId === user.uid ? (lang === "es" ? "Tú" : "You") : (gifterMember?.name || "?"),
          recipientName: recipientMember.name,
          isMe: gifterId === user.uid,
        });
      }
    }
    setSantaRevealName(reveals);
    setShowSantaEnvelope(true);
  };

  // ── Cargar wishlist names para el picker de grupos ──
  const loadWishlistNames = async () => {
    const wlIds = userProfile?.wishlists || [];
    if (wlIds.length === 0) return;
    const snaps = await Promise.all(wlIds.map((id) => getDoc(doc(db, "wishlists", id))));
    const names = {};
    snaps.forEach((s) => { if (s.exists()) names[s.id] = s.data().name; });
    setWishlistNames(names);
  };

  return {
    // estado
    currentFamily,
    setCurrentFamily,
    userFamilies,
    setUserFamilies,
    members,
    setMembers,
    groupIcons,
    isLeader,

    // modales ui
    showAddManaged, setShowAddManaged,
    showEditName, setShowEditName,
    showSantaConfirm, setShowSantaConfirm,
    showSantaEnvelope, setShowSantaEnvelope,
    editingManaged, setEditingManaged,
    editingNickname, setEditingNickname,
    nicknameValue, setNicknameValue,
    managedName, setManagedName,
    managedColor, setManagedColor,
    santaBudget, setSantaBudget,
    santaRevealName,
    editNameValue, setEditNameValue,
    editNameOpenedFrom, setEditNameOpenedFrom,
    editingGroupIcon, setEditingGroupIcon,
    popupFamily, setPopupFamily,
    wishlistNames,

    // handlers
    getDisplayName,
    saveGroupNickname,
    handleAddManagedMember,
    saveEditedManaged,
    deleteManagedMember,
    saveGroupName,
    saveGroupIcon,
    deleteGroup,
    leaveGroup,
    activateSantaSecreto,
    deactivateSantaSecreto,
    openSantaEnvelope,
    loadWishlistNames,
  };
}
